import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { countSuppressedEmails } from '../db/baseql/suppression-ops.js';
import type { CampaignDraft, ChannelFallback, SalesRep } from './types.js';

/** Extract "HH:mm" from a Date, using the Date's local hours/minutes. */
function formatTimeHHmm(d: Date): string {
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface WizSummaryProps {
  draft: CampaignDraft;
  /**
   * Called after the prepare endpoint accepts the request. The wizard host should
   * navigate to the Campaign Detail page where the user reviews/edits the
   * generated messages and triggers the actual launch.
   */
  onPrepared?: (campaignId: string) => void;
}

type PrepareStatus = 'idle' | 'submitting' | 'error';

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
interface CheckItem {
  label: string;
  ok: boolean;
  detail: string;
}

const validateDraft = (draft: CampaignDraft): CheckItem[] => {
  const checks: CheckItem[] = [];

  // Campaign type is now derived from the sequence (see `deriveCampaignType`),
  // so we just surface the resulting label instead of asking the user to pick
  // it. The check always passes unless the sequence is empty (covered by the
  // "Sequence" check further down).
  if (draft.campaignType) {
    checks.push({
      label: 'Campaign Type',
      ok: true,
      detail:
        draft.campaignType === 'multi'
          ? 'Cross-Channel (Email + LinkedIn) — derived from sequence'
          : `${draft.campaignType.charAt(0).toUpperCase() + draft.campaignType.slice(1)} — derived from sequence`,
    });
  }

  // Only the product/service name is required; the description and talking
  // points are optional context, so the offering is valid with just a name.
  const hasOffering = !!draft.offering.name.trim();
  checks.push({
    label: 'Offering',
    ok: hasOffering,
    detail: hasOffering ? draft.offering.name : 'Missing product or service name',
  });

  const hasApolloSourcing = !!draft.apolloSearchInput;
  checks.push({
    label: 'Leads',
    ok: draft.leads.length > 0 || hasApolloSourcing,
    detail:
      draft.leads.length > 0
        ? `${draft.leads.length} leads`
        : hasApolloSourcing
          ? 'Apollo sourcing configured'
          : 'No leads uploaded',
  });

  const hasDays = draft.schedule.sendDays.length > 0;
  checks.push({
    label: 'Schedule',
    ok: hasDays,
    detail: hasDays ? `${draft.schedule.sendDays.length} days, limit ${draft.schedule.dailyLimit}/day` : 'No send days',
  });

  checks.push({
    label: 'Sequence',
    ok: draft.sequence.length > 0,
    detail: draft.sequence.length > 0 ? `${draft.sequence.length} steps` : 'No steps defined',
  });

  checks.push({
    label: 'Sales Representatives',
    ok:
      draft.aiEmployeeAssignments.length > 0 &&
      draft.aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0) === 100 &&
      draft.aiEmployeeAssignments.every(a => a.percentage > 0),
    detail:
      draft.aiEmployeeAssignments.length === 0
        ? 'No Sales Representative selected'
        : draft.aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0) !== 100
          ? `Percentages must total 100% (currently ${draft.aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0)}%)`
          : draft.aiEmployeeAssignments.some(a => a.percentage <= 0)
            ? 'Each assigned Sales Representative must have a share > 0%'
            : `${draft.aiEmployeeAssignments.length} Sales Rep${draft.aiEmployeeAssignments.length > 1 ? 's' : ''} (${draft.aiEmployeeAssignments.map(a => `${a.percentage}%`).join(' / ')})`,
  });

  if (draft.campaignType === 'email') {
    const missing = draft.aiEmployeeAssignments.filter(
      a => (draft.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0,
    );
    checks.push({
      label: 'Email Accounts',
      ok: draft.aiEmployeeAssignments.length > 0 && missing.length === 0,
      detail:
        missing.length > 0
          ? `${missing.length} Sales Rep(s) without selected accounts`
          : `${draft.emailAccounts.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
    });
  } else if (draft.campaignType === 'linkedin') {
    const missing = draft.aiEmployeeAssignments.filter(
      a => (draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0,
    );
    checks.push({
      label: 'LinkedIn Accounts',
      ok: draft.aiEmployeeAssignments.length > 0 && missing.length === 0,
      detail:
        missing.length > 0
          ? `${missing.length} Sales Rep(s) without selected accounts`
          : `${draft.linkedinAccountIds.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
    });
  } else if (draft.campaignType === 'multi') {
    const hasEmailStep = draft.sequence.some(s => s.channel === 'email');
    const hasLiStep = draft.sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
    const missingEmail = hasEmailStep
      ? draft.aiEmployeeAssignments.filter(a => (draft.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0)
      : [];
    const missingLi = hasLiStep
      ? draft.aiEmployeeAssignments.filter(a => (draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0)
      : [];
    // In multi-mode a Sales Rep only fully-fails validation if it has neither channel
    // (then it cannot send anything at all). Partial coverage is allowed but
    // surfaced via the dedicated Channel Coverage warning panel below.
    const orphanSalesReps = draft.aiEmployeeAssignments.filter(a => {
      const noEmail = (draft.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0;
      const noLi = (draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0;
      return noEmail && noLi;
    });
    if (hasEmailStep) {
      const partial = missingEmail.length > 0 && missingEmail.length < draft.aiEmployeeAssignments.length;
      checks.push({
        label: 'Email Accounts',
        ok: draft.aiEmployeeAssignments.length > 0 && missingEmail.length < draft.aiEmployeeAssignments.length,
        detail:
          missingEmail.length === draft.aiEmployeeAssignments.length
            ? 'No Sales Representative has an email account configured'
            : partial
              ? `${draft.emailAccounts.length} account(s) — ${missingEmail.length} Sales Rep(s) without email (see coverage warning)`
              : `${draft.emailAccounts.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
      });
    }
    if (hasLiStep) {
      const partial = missingLi.length > 0 && missingLi.length < draft.aiEmployeeAssignments.length;
      checks.push({
        label: 'LinkedIn Accounts',
        ok: draft.aiEmployeeAssignments.length > 0 && missingLi.length < draft.aiEmployeeAssignments.length,
        detail:
          missingLi.length === draft.aiEmployeeAssignments.length
            ? 'No Sales Representative has a LinkedIn account configured'
            : partial
              ? `${draft.linkedinAccountIds.length} account(s) — ${missingLi.length} Sales Rep(s) without LinkedIn (see coverage warning)`
              : `${draft.linkedinAccountIds.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
      });
    }
    if (orphanSalesReps.length > 0) {
      checks.push({
        label: 'Sales Rep Channels',
        ok: false,
        detail: `${orphanSalesReps.length} Sales Rep(s) have no email AND no LinkedIn account — they cannot send anything.`,
      });
    }
    if (!hasEmailStep && !hasLiStep) {
      checks.push({
        label: 'Channels',
        ok: false,
        detail: 'Cross-channel campaign needs at least one Email or LinkedIn step.',
      });
    }
  }

  checks.push({ label: 'Auto-Reply', ok: true, detail: draft.autoReply ? 'Enabled' : 'Disabled' });

  return checks;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const WizSummary = stateful((props: WizSummaryProps) => {
  const { draft, onPrepared } = props;

  const [prepareStatus, setPrepareStatus] = useState<PrepareStatus>('idle');
  const [prepareError, setPrepareError] = useState('');
  const [acknowledgedCoverage, setAcknowledgedCoverage] = useState(false);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  /**
   * Per-gap fallback selection. Key = `${sourceAieId}::${channel}`,
   * value = fallback Sales Rep id (or '' for "no fallback — silently skip").
   */
  const [fallbackChoices, setFallbackChoices] = useState<Record<string, string>>({});

  // Seed the dropdowns from fallbacks persisted on the campaign (edit of a
  // prepared campaign / duplicated campaign). Without this the warning always
  // reset to "Skip" even though the draft carries channelFallbacks.
  useEffect(() => {
    const persisted: Record<string, string> = {};
    for (const f of draft.channelFallbacks ?? []) {
      if (f?.sourceAiEmployeeId && f?.channel && f?.fallbackAiEmployeeId) {
        persisted[`${f.sourceAiEmployeeId}::${f.channel}`] = f.fallbackAiEmployeeId;
      }
    }
    if (Object.keys(persisted).length === 0) return;
    // User picks win over persisted values.
    setFallbackChoices({ ...persisted, ...fallbackChoices });
  }, [JSON.stringify(draft.channelFallbacks ?? [])]);

  // Lazy-load Sales Rep list so we can show readable names in the coverage warning.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetch('/api/ai-employees');
        const data = (await resp.json()) as { ok: boolean; aiEmployees?: SalesRep[] };
        if (!cancelled && data.ok) setSalesReps(data.aiEmployees ?? []);
      } catch {
        /* ignore — names will fall back to Sales Rep id */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const salesRepName = (id: string): string => salesReps.find(e => e._id === id)?.name ?? `Sales Rep ${id.slice(-6)}`;

  // ── Coverage gaps (multi-mode only) ─────────────────────────────────────
  // For each Sales Rep that has at least one channel but is missing the other,
  // report which channel they cannot serve and how many leads (estimated by
  // their percentage share) will skip that channel.
  interface CoverageGap {
    aiEmployeeId: string;
    missing: 'email' | 'linkedin';
    percentage: number;
    estimatedLeads: number;
  }
  const coverageGaps: CoverageGap[] = (() => {
    if (draft.campaignType !== 'multi') return [];
    const hasEmailStep = draft.sequence.some(s => s.channel === 'email');
    const hasLiStep = draft.sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
    const totalLeads = draft.leads.length;
    const gaps: CoverageGap[] = [];
    for (const a of draft.aiEmployeeAssignments) {
      const emails = draft.emailAccountsByAie[a.aiEmployeeId] ?? [];
      const linkedins = draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? [];
      const estimated = Math.round((a.percentage / 100) * totalLeads);
      if (hasEmailStep && emails.length === 0 && linkedins.length > 0) {
        gaps.push({
          aiEmployeeId: a.aiEmployeeId,
          missing: 'email',
          percentage: a.percentage,
          estimatedLeads: estimated,
        });
      }
      if (hasLiStep && linkedins.length === 0 && emails.length > 0) {
        gaps.push({
          aiEmployeeId: a.aiEmployeeId,
          missing: 'linkedin',
          percentage: a.percentage,
          estimatedLeads: estimated,
        });
      }
    }
    return gaps;
  })();

  const checks = validateDraft(draft);
  const allValid = checks.every(c => c.ok);

  // ── Suppression check: how many leads are on the Do-Not-Contact list? ──
  const leadEmails = draft.leads.map(l => l.email).filter(Boolean) as string[];
  const suppressionQuery = useQuery(
    () => countSuppressedEmails(leadEmails)({ suppressedCount: 1, suppressedEmails: 1 }),
    [draft.leads.length],
  );
  const suppressedCount = suppressionQuery.value?.suppressedCount ?? 0;

  // Build channelFallbacks payload from the user's per-gap dropdown picks.
  const channelFallbacks: ChannelFallback[] = coverageGaps
    .map(g => {
      const key = `${g.aiEmployeeId}::${g.missing}`;
      const fb = fallbackChoices[key];
      if (!fb) return null;
      return {
        sourceAiEmployeeId: g.aiEmployeeId,
        channel: g.missing,
        fallbackAiEmployeeId: fb,
      } satisfies ChannelFallback;
    })
    .filter((f): f is ChannelFallback => f !== null);

  // Gaps that are NOT covered by a fallback still require explicit acknowledgement.
  const unresolvedGaps = coverageGaps.filter(g => !fallbackChoices[`${g.aiEmployeeId}::${g.missing}`]);
  const requiresAcknowledgement = unresolvedGaps.length > 0;
  const canGenerate = allValid && prepareStatus !== 'submitting' && (!requiresAcknowledgement || acknowledgedCoverage);

  // Helper: which Sales Reps can serve as fallback for a given missing channel?
  const fallbackCandidates = (missing: 'email' | 'linkedin', sourceSalesRepId: string): string[] => {
    const bucket = missing === 'email' ? draft.emailAccountsByAie : draft.linkedinAccountIdsByAie;
    return draft.aiEmployeeAssignments
      .filter(a => a.aiEmployeeId !== sourceSalesRepId && (bucket[a.aiEmployeeId] ?? []).length > 0)
      .map(a => a.aiEmployeeId);
  };

  // ── Generate Messages (Phase A) ─────────────────────────────────────────
  const generateMessages = async () => {
    if (prepareStatus === 'submitting') return;
    setPrepareStatus('submitting');
    setPrepareError('');

    try {
      const resp = await fetch('/api/campaign/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft: {
            _id: draft._id,
            campaignName: draft.name,
            campaignType: draft.campaignType,
            offering: draft.offering,
            leads: draft.leads,
            schedule: {
              timezone: draft.schedule.timezone,
              startTime: formatTimeHHmm(draft.schedule.startTime),
              endTime: formatTimeHHmm(draft.schedule.endTime),
              sendDays: draft.schedule.sendDays,
              dailyLimit: draft.schedule.dailyLimit,
              autoDmOnAccept: draft.schedule.autoDmOnAccept ?? true,
            },
            sequence: draft.sequence,
            emailAccounts: draft.emailAccounts,
            linkedinAccountIds: draft.linkedinAccountIds,
            aiEmployeeId: draft.aiEmployeeId,
            aiEmployeeAssignments: draft.aiEmployeeAssignments,
            emailAccountsByAie: Object.entries(draft.emailAccountsByAie).map(([aiEmployeeId, emailAccounts]) => ({
              aiEmployeeId,
              emailAccounts,
            })),
            linkedinAccountIdsByAie: Object.entries(draft.linkedinAccountIdsByAie).map(
              ([aiEmployeeId, linkedinAccountIds]) => ({ aiEmployeeId, linkedinAccountIds }),
            ),
            channelFallbacks,
            autoReply: draft.autoReply ?? false,
            replyPrompt: draft.replyPrompt || undefined,
            crmAutoSync: draft.crmAutoSync ?? false,
            language: draft.language || undefined,
            apolloSearchInput: draft.apolloSearchInput,
            projectId: draft.projectId,
          },
        }),
      });

      const data = (await resp.json()) as { ok: boolean; campaignId?: string; error?: string };
      if (!data.ok || !data.campaignId) {
        setPrepareStatus('error');
        setPrepareError(data.error ?? 'Prepare failed');
        return;
      }
      onPrepared?.(data.campaignId);
    } catch (e) {
      setPrepareStatus('error');
      setPrepareError(e instanceof Error ? e.message : 'Network error');
    }
  };

  // ── Render Helpers ──────────────────────────────────────────────────────
  const campaignChecklist = () => html`
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center gap-3">
          <span class="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
            <span class="icon-[material-symbols--summarize] text-xl"></span>
          </span>
          <h2 class="card-title text-lg">Campaign Summary</h2>
        </div>
        <div class="mt-2 flex flex-col gap-1.5">
          ${checks.map(
            c => html`
              <div class="flex items-center gap-2 text-sm">
                <span
                  class="${
                    c.ok
                      ? 'icon-[material-symbols--check-circle] text-success'
                      : 'icon-[material-symbols--cancel] text-error'
                  } shrink-0 text-lg"
                ></span>
                <span class="font-medium">${c.label}:</span>
                <span class="${c.ok ? 'text-base-content/70' : 'text-error/70'}">${c.detail}</span>
              </div>
            `,
          )}
        </div>
        ${
          !allValid
            ? html`<div class="alert alert-warning mt-3 text-sm">
              <span class="icon-[material-symbols--warning]"></span>
              Please complete all steps before finishing setup.
            </div>`
            : nothing
        }
      </div>
    </div>
  `;

  const generateSection = () => {
    return html`
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <h2 class="card-title text-lg">Finish Setup</h2>

          ${
            prepareStatus === 'error'
              ? html`
                <div class="alert alert-error text-sm">
                  <span class="icon-[material-symbols--error]"></span>
                  ${prepareError}
                </div>
              `
              : nothing
          }

          <p class="text-base-content/70 text-sm">
            Please review your campaign configuration above. If you're happy with everything, complete the setup to
            switch to the campaign dashboard where you can launch the campaign.
          </p>

          <div class="card-actions mt-4 justify-end">
            <button class="btn btn-primary gap-1" ?disabled=${!canGenerate} @click=${() => void generateMessages()}>
              <span class="icon-[material-symbols--check-circle]"></span> Finish Setup
            </button>
          </div>
        </div>
      </div>
    `;
  };

  const coverageWarning = () => {
    if (coverageGaps.length === 0) return nothing;
    return html`
      <div class="card border-warning/40 bg-warning/5 border shadow-sm">
        <div class="card-body gap-3">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols--warning] text-warning text-xl"></span>
            <h2 class="card-title text-warning text-lg">Channel Coverage Warning</h2>
          </div>
          <p class="text-base-content/80 text-sm">
            In Cross-Channel campaigns each lead is permanently assigned to exactly one Sales Representative and only
            ever receives messages from that Sales Representative's accounts. Some of your Sales Representatives are
            missing a channel — choose another Sales Representative's account to send those steps, or accept that the
            steps will be silently skipped.
          </p>
          <ul class="space-y-2">
            ${coverageGaps.map(g => {
              const key = `${g.aiEmployeeId}::${g.missing}`;
              const channelLabel = g.missing === 'email' ? 'Email' : 'LinkedIn';
              const candidates = fallbackCandidates(g.missing, g.aiEmployeeId);
              const selected = fallbackChoices[key] ?? '';
              return html`
                <li class="bg-base-100 rounded-lg p-3 text-sm">
                  <div class="font-semibold">${salesRepName(g.aiEmployeeId)}</div>
                  <div class="text-base-content/70 mt-0.5">
                    No <strong>${channelLabel}</strong> account configured — approx.
                    <strong>${g.estimatedLeads}</strong> lead${g.estimatedLeads === 1 ? '' : 's'} (${g.percentage}%
                    share) affected.
                  </div>
                  ${
                    candidates.length > 0
                      ? html`
                        <div class="mt-2 flex flex-wrap items-center gap-2">
                          <label class="text-base-content/70 text-xs"> Send ${channelLabel} steps via: </label>
                          <select
                            class="select select-bordered select-xs"
                            .value=${selected}
                            @change=${(e: Event) => {
                              const value = (e.target as HTMLSelectElement).value;
                              setFallbackChoices({ ...fallbackChoices, [key]: value });
                              if (value) setAcknowledgedCoverage(false);
                            }}
                          >
                            <option value="">— Skip ${channelLabel} steps for these leads —</option>
                            ${candidates.map(
                              c => html`
                                <option value=${c} ?selected=${selected === c}>
                                  ${salesRepName(c)}'s ${channelLabel} account
                                </option>
                              `,
                            )}
                          </select>
                          ${
                            selected
                              ? html`
                                <span class="text-success text-xs">
                                  <span class="icon-[material-symbols--check-circle] align-text-bottom"></span>
                                  Identity stays ${salesRepName(g.aiEmployeeId)} — sending account taken from
                                  ${salesRepName(selected)}.
                                </span>
                              `
                              : nothing
                          }
                        </div>
                      `
                      : html`
                        <div class="text-base-content/60 mt-1 text-xs">
                          No other Sales Representative has a ${channelLabel} account configured — these steps will be
                          skipped.
                        </div>
                      `
                  }
                </li>
              `;
            })}
          </ul>
          ${
            requiresAcknowledgement
              ? html`
                <label class="label mt-1 cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-warning checkbox-sm"
                    .checked=${acknowledgedCoverage}
                    @change=${(e: Event) => setAcknowledgedCoverage((e.target as HTMLInputElement).checked)}
                  />
                  <span class="label-text text-sm">
                    I understand that some leads will not receive all channels (no fallback chosen).
                  </span>
                </label>
              `
              : html`
                <div class="alert alert-success text-xs">
                  <span class="icon-[material-symbols--check-circle]"></span>
                  All coverage gaps are covered by a fallback Sales Rep account. Ready to generate.
                </div>
              `
          }
        </div>
      </div>
    `;
  };

  const linkedinSafetyInfo = () => {
    if (draft.campaignType !== 'linkedin') return nothing;
    return html`
      <div class="card border-info/30 bg-info/5 border shadow-sm">
        <div class="card-body gap-3">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols--shield-lock] text-info text-xl"></span>
            <h2 class="card-title text-info text-lg">LinkedIn Account Protection</h2>
          </div>
          <p class="text-base-content/70 text-sm">
            To prevent your LinkedIn account from being flagged or banned, all outreach actions are sent through a
            safety engine with the following measures:
          </p>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div class="bg-base-100 rounded-lg p-3">
              <div class="flex items-center gap-1.5 text-sm font-semibold">
                <span class="icon-[material-symbols--speed] text-warning"></span>
                Daily Limits
              </div>
              <ul class="text-base-content/70 mt-1.5 space-y-0.5 text-xs">
                <li>Max 20 connection requests/day</li>
                <li>Max 50 DMs/day</li>
                <li>Max 80 total actions/day</li>
                <li>New accounts: reduced limits for first 2 weeks</li>
              </ul>
            </div>
            <div class="bg-base-100 rounded-lg p-3">
              <div class="flex items-center gap-1.5 text-sm font-semibold">
                <span class="icon-[material-symbols--timer] text-primary"></span>
                Human-like Delays
              </div>
              <ul class="text-base-content/70 mt-1.5 space-y-0.5 text-xs">
                <li>15–180s pause between each lead</li>
                <li>5–15 min break every 8–15 actions</li>
                <li>Min. 20 min between sequence steps</li>
                <li>Randomized timing (no fixed patterns)</li>
              </ul>
            </div>
            <div class="bg-base-100 rounded-lg p-3">
              <div class="flex items-center gap-1.5 text-sm font-semibold">
                <span class="icon-[material-symbols--emergency-home] text-error"></span>
                Auto Circuit Breaker
              </div>
              <ul class="text-base-content/70 mt-1.5 space-y-0.5 text-xs">
                <li>1 rate-limit warning → 1h pause</li>
                <li>2 warnings/day → stop until next day</li>
                <li>Only sends during your business hours</li>
                <li>Auto-retry with increasing backoff</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  // Non-blocking warning: the sequence has an email step but some leads have no
  // email address (e.g. LinkedIn-only imports). Those leads still receive the
  // LinkedIn steps; they are simply skipped for the email step(s). No warning is
  // shown when there is no email step (LinkedIn-only campaigns are fully fine).
  const emailCoverageWarning = () => {
    const hasEmailStep = draft.sequence.some(s => s.channel === 'email');
    if (!hasEmailStep) return nothing;
    const missingEmailCount = draft.leads.filter(l => !(l.email ?? '').trim()).length;
    if (missingEmailCount === 0) return nothing;
    const total = draft.leads.length;
    return html`
      <div class="card border-warning/40 bg-warning/5 border shadow-sm">
        <div class="card-body gap-2">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols--warning] text-warning text-xl"></span>
            <h2 class="card-title text-warning text-lg">Some leads have no email address</h2>
          </div>
          <p class="text-base-content/80 text-sm">
            <strong>${missingEmailCount}</strong> of <strong>${total}</strong>
            lead${total === 1 ? '' : 's'} have no email address, but your sequence contains an email step. These leads
            will be skipped for the email step(s) — they still receive the LinkedIn steps when a LinkedIn URL is
            present. Remove the email step or add email addresses if every lead should receive it.
          </p>
        </div>
      </div>
    `;
  };

  return html`
    <div class="flex flex-col gap-4">
      ${campaignChecklist()} ${coverageWarning()} ${emailCoverageWarning()} ${linkedinSafetyInfo()}
      ${
        suppressedCount > 0
          ? html`
            <div class="card border-info/30 bg-info/5 border shadow-sm">
              <div class="card-body">
                <div class="flex items-center gap-2">
                  <span class="icon-[material-symbols--block] text-info text-xl"></span>
                  <h2 class="card-title text-info text-lg">Suppressed Contacts</h2>
                </div>
                <p class="text-base-content/70 text-sm">
                  <strong>${suppressedCount}</strong> contact${suppressedCount === 1 ? '' : 's'} on this campaign's lead
                  list have opted out of all outreach. They stay on the list, marked opted out — no message is generated
                  or sent to them.
                </p>
              </div>
            </div>
          `
          : nothing
      }
      ${generateSection()}
    </div>
  `;
});
